import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import type { AccessLevel } from "@/lib/enums";
import { accessKeys } from "@/lib/query";

/** One row of Settings -> Access: a grant, plus the person once they have signed in. */
export interface AccessGrant {
  id: string;
  email: string;
  level: AccessLevel;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  userId: string | null;
  name: string | null;
  addedByName: string | null;
  lastSignInAt: string | null;
}

interface AccessListResponse {
  data: AccessGrant[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface AccessGrantInput {
  email: string;
  level: AccessLevel;
  title?: string | null;
}

/** Whether the change also reached the Claude connector's allowlist. */
type ConnectorSync = "ok" | "skipped" | "failed";

async function errorFrom(res: Response, fallback: string): Promise<Error> {
  const json = await res.json().catch(() => null);
  return new Error(typeof json?.error === "string" ? json.error : fallback);
}

/**
 * Every access change is saved here first, then mirrored to the list the
 * Claude connector reads. Say which happened: a change that reached the app
 * but not Claude means Claude answers for the old list until the server's
 * five-minute reconcile (src/lib/access.ts) repairs the mirror.
 */
function announce(message: string, sync: ConnectorSync) {
  if (sync === "ok") {
    toast.success(message, { description: "Claude connector updated." });
  } else if (sync === "skipped") {
    toast.success(message, {
      description: "Claude connector not updated: syncing is off in this environment.",
    });
  } else {
    toast.success(message);
    toast.warning("Claude connector not updated yet", {
      description:
        "The change applied to the dashboard. The Claude connector will catch up automatically within 5 minutes.",
      duration: 15_000,
    });
  }
}

export function useAccessGrants(query: DataGridQuery, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...accessKeys.list(query), pageRequest] as const,
    queryFn: async (): Promise<AccessListResponse> => {
      const res = await fetch(`/api/admin/access?${buildListParams(query, pageRequest)}`);
      if (!res.ok) throw await errorFrom(res, "Failed to load access");
      return res.json();
    },
  });
}

export function useCreateAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AccessGrantInput): Promise<ConnectorSync> => {
      const res = await fetch("/api/admin/access", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await errorFrom(res, "Failed to add person");
      return (await res.json()).connectorSync;
    },
    onSuccess: (sync, input) => {
      queryClient.invalidateQueries({ queryKey: accessKeys.all });
      announce(`${input.email} can now sign in`, sync);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      grant: AccessGrant;
      level?: AccessLevel;
      title?: string | null;
    }): Promise<ConnectorSync> => {
      const res = await fetch(`/api/admin/access/${input.grant.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ level: input.level, title: input.title }),
      });
      if (!res.ok) throw await errorFrom(res, "Failed to update access");
      return (await res.json()).connectorSync;
    },
    onSuccess: (sync, input) => {
      queryClient.invalidateQueries({ queryKey: accessKeys.all });
      announce(`Access updated for ${input.grant.name ?? input.grant.email}`, sync);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useRemoveAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (grant: AccessGrant): Promise<ConnectorSync> => {
      const res = await fetch(`/api/admin/access/${grant.id}`, { method: "DELETE" });
      if (!res.ok) throw await errorFrom(res, "Failed to remove access");
      return (await res.json()).connectorSync;
    },
    onSuccess: (sync, grant) => {
      queryClient.invalidateQueries({ queryKey: accessKeys.all });
      announce(`${grant.name ?? grant.email} no longer has access`, sync);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}
